import h from "../helpers";
import { request } from "../helpers/httpInterceptor";
import { notifyGreen } from "./notifyActions";
import { Dispatch, GetState } from "../types/types";
import {
    IAppSettings,
    IAppSettingsList,
    ISetAppSettings,
    ISetAppSettingsList,
    ISetIsSavingAppSettings,
    ISetLoadingAppSettingsList,
    ISetLoadingAppSettings,
    ISetSaveCompletedAppSettings,
} from "../types/stores/appSettings";
import {
    SET_APP_SETTINGS,
    SET_APP_SETTINGS_LIST,
    SET_IS_SAVING_APP_SETTINGS,
    SET_LOADING_APP_SETTINGS,
    SET_LOADING_APP_SETTINGS_LIST,
    SET_SAVE_COMPLETED_APP_SETTINGS,
} from "../reducers/appSettings";

export const SetAppSettingsIsLoading = (newValue: boolean): ISetLoadingAppSettings => ({
    type: SET_LOADING_APP_SETTINGS,
    isLoading: newValue,
});

export const SetAppSettingListingIsLoading = (newValue: boolean): ISetLoadingAppSettingsList => ({
    type: SET_LOADING_APP_SETTINGS_LIST,
    isLoadingList: newValue,
});

export const SetAppSettingsIsSaving = (newValue: boolean): ISetIsSavingAppSettings => ({
    type: SET_IS_SAVING_APP_SETTINGS,
    isSaving: newValue,
});

export const SetAppSettingsSaveCompleted = (newValue: boolean): ISetSaveCompletedAppSettings => ({
    type: SET_SAVE_COMPLETED_APP_SETTINGS,
    completed: newValue,
});

export const SetAppSettingsFields = (data: Array<IAppSettings>): ISetAppSettings => ({
    type: SET_APP_SETTINGS,
    appSettings: data,
});

export const SetAppSettingList = (data: Array<IAppSettingsList>): ISetAppSettingsList => ({
    type: SET_APP_SETTINGS_LIST,
    appSettingsList: data,
});

export const GetAppSettingByCompanyId =
    (settingName: string, companyId: number) =>
    async (dispatch: Dispatch, getState: GetState): Promise<void> => {
        const state = getState();
        let appSettings = state.appSettings.appSettings || [];

        // make sure we get app settings
        if (!appSettings || (appSettings && appSettings.length <= 0)) {
            appSettings = await dispatch(GetAppSettings(companyId));
        }

        // filter out by company Id
        const foundAppSetting =
            typeof appSettings != "undefined" &&
            appSettings.find(setting => setting.Name === settingName && setting.CompanyId == companyId);
        return foundAppSetting;
    };

export const GetAppSettings =
    // companyId needs to be undefined when all companies is selected


        (companyId?: number) =>
        (dispatch: Dispatch): any => {
            dispatch(SetAppSettingsIsLoading(true));
            return request(
                `/api/app-settings/all?companyId=${companyId}`,
                {
                    credentials: "same-origin",
                    method: "GET",
                },
                dispatch
            )
                .then(h.checkStatus)
                .then(h.toJson)
                .then(data => {
                    dispatch(SetAppSettingsIsLoading(false));

                    if (data && data.success) {
                        dispatch(SetAppSettingsFields(data.settings));
                        return data.settings;
                    }
                })
                .catch(error => {
                    dispatch(SetAppSettingsIsLoading(false));

                    h.error("Error getting app settings.", error);
                });
        };

export const SaveNewDefault =
    (
        defaultSettingName: string,
        defaultSettingDescription: string,
        defaultSettingActive: boolean,
        defaultNoEncryption: boolean,
        defaultSettingMaxLength: number,
        defaultSettingValue: string
    ) =>
    (dispatch: Dispatch): any => {
        if (!defaultSettingName) dispatch(SetAppSettingsIsSaving(true));
        return request(
            `/api/app-settings/default`,
            {
                credentials: "same-origin",
                method: "POST",
                body: JSON.stringify({
                    SettingName: defaultSettingName,
                    SettingDescription: defaultSettingDescription,
                    SettingMaxLength: defaultSettingMaxLength,
                    SettingActive: defaultSettingActive ? "Y" : "N",
                    NoEncryption: defaultNoEncryption ? 1 : 0,
                    DefaultValue: defaultSettingValue || "",
                }),
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                dispatch(SetAppSettingsIsSaving(false));
                dispatch(SetAppSettingsSaveCompleted(true));

                setTimeout(() => {
                    dispatch(SetAppSettingsSaveCompleted(false));
                }, 200);

                if (data && data.success) {
                    dispatch(notifyGreen("Successfully saved your new default setting."));
                } else if (!data.success && data.message) {
                    return data.message;
                }
            })
            .catch(error => {
                dispatch(SetAppSettingsIsSaving(false));
                h.error("Error saving new default setting.", error);
            });
    };

export const SaveNewAppSetting =
    (
        selectedCompany: number,
        selectedSettingID: number,
        settingValue: string,
        maskSettingValue: boolean,
        isEncryption: boolean
    ) =>
    (dispatch: Dispatch): any => {
        if (!selectedCompany || !selectedSettingID || !settingValue) return;

        dispatch(SetAppSettingsIsSaving(true));
        return request(
            `/api/app-settings`,
            {
                credentials: "same-origin",
                method: "POST",
                body: JSON.stringify({
                    CompanyId: selectedCompany,
                    SettingId: selectedSettingID,
                    SettingValue: settingValue,
                    MaskSettingValue: maskSettingValue,
                    IsEncryption: isEncryption,
                }),
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                dispatch(SetAppSettingsIsSaving(false));
                dispatch(SetAppSettingsSaveCompleted(true));

                setTimeout(() => {
                    dispatch(SetAppSettingsSaveCompleted(false));
                }, 200);

                if (data && data.success) {
                    dispatch(notifyGreen("Successfully saved your new setting."));
                } else if (!data.success && data.message) {
                    return data.message;
                }
            })
            .catch(error => {
                dispatch(SetAppSettingsIsSaving(false));
                h.error("Error saving new app settings.", error);
            });
    };

export const GetAppSettingsList =
    () =>
    (dispatch: Dispatch): any => {
        dispatch(SetAppSettingListingIsLoading(true));

        return request(
            "/api/app-settings/list",
            {
                credentials: "same-origin",
                method: "GET",
            },
            dispatch
        )
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                dispatch(SetAppSettingListingIsLoading(false));
                if (data && data.success) {
                    dispatch(SetAppSettingList(data.list));
                }
            })
            .catch(error => {
                dispatch(SetAppSettingListingIsLoading(false));

                h.error("Error getting app settings' settings listing.", error);
            });
    };

export const SaveAppSettings =
    // selectedCompany needs to be undefined when all companies is selected


        (appSettingsToChanged: Array<any> = [], selectedCompany?: number) =>
        (dispatch: Dispatch): any => {
            if (appSettingsToChanged && typeof appSettingsToChanged != "undefined") {
                dispatch(SetAppSettingsIsSaving(true));
                return request(
                    `/api/app-settings`,
                    {
                        credentials: "same-origin",
                        method: "PUT",
                        body: JSON.stringify({
                            settings: appSettingsToChanged,
                        }),
                    },
                    dispatch
                )
                    .then(h.checkStatus)
                    .then(h.toJson)
                    .then(data => {
                        dispatch(SetAppSettingsIsSaving(false));
                        dispatch(SetAppSettingsSaveCompleted(true));

                        setTimeout(() => {
                            dispatch(SetAppSettingsSaveCompleted(false));
                        }, 200);

                        if (data && data.success) {
                            dispatch(notifyGreen("Successfully saved your changes."));
                            dispatch(GetAppSettings(selectedCompany));
                        } else if (data && !data.success) {
                            dispatch(SetAppSettingsIsSaving(false));
                            h.error("Error saving app settings.", data.message);
                        }
                    })
                    .catch(error => {
                        dispatch(SetAppSettingsIsSaving(false));
                        h.error("Error saving app settings.", error);
                    });
            }
        };
